
import { Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { OnboardingProductsStore } from '../../../store/store';
import {
  NationalityManagerCreateActions,
  UiActions
} from '../../../store/actions';
import { DataNotClientService } from '../../../services/data-not-client.service';
import { LoadingService } from '../../../services/loading.service';
import { SessionService } from '../../../services/session.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RequestNationalityManagerCreate } from 'src/app/shared/interfaces';
import { RedirectService } from 'src/app/services/redirect.service';
import { ApiResponseStatus, StepsClientTypes, StepsNotClientTypes } from '../../../shared/enums';
import { countries } from '../data/countries';
import { environment } from 'src/environments/environment';
import { TagPage } from 'src/app/store/actions/ui.actions';
import { DataClientService } from 'src/app/services/data-client.service';

@Component({
  selector: 'app-nationality',
  templateUrl: './nationality.component.html',
  styleUrls: ['../../../global.scss', './nationality.component.scss']
})
export class NationalityComponent implements OnDestroy, OnInit {
  mainIsClient: boolean;
  countries: any = countries;
  countriesNationality: any;
  formData: FormGroup;
  privateMode = false;
  dataService: DataNotClientService | DataClientService;

  nationalityManager$ = this.store.pipe(
    select(OnboardingProductsStore.selectors.nationalityManagerCreate)
  );

  constructor(
    private store: Store<OnboardingProductsStore.AppState>,
    private dataNotClientService: DataNotClientService,
    private dataClientService: DataClientService,
    public loadingService: LoadingService,
    public sessionService: SessionService,
    private _fb: FormBuilder,
    private redirectService: RedirectService
  ) {
    this.setService();
    this.setForm();
  }

  nationalityManagerSuscription = this.nationalityManager$.subscribe(({ response, error }) => {
    // error case
    if (error != null) {
      this.redirectService.setRedirect(StepsNotClientTypes.STEP_PUBLIC_LANDING);
      this.store.dispatch(NationalityManagerCreateActions.cleanState());
      const step = this.privateMode ? StepsClientTypes.STEP_SERVICE_ERROR : StepsNotClientTypes.STEP_SERVICE_ERROR;
      this.store.dispatch(UiActions.Navigate({ step }));
    }

    if (response) {
      this.store.dispatch(NationalityManagerCreateActions.cleanState());
      if (response.client.operationState.code === ApiResponseStatus.OK) {
        this.store.dispatch(UiActions.Navigate({step: this.navigation()}));
      } else {
        const redirect = this.privateMode ? StepsClientTypes.STEP_SERVICE_ERROR : StepsNotClientTypes.STEP_SERVICE_ERROR;
        this.store.dispatch(UiActions.Navigate({step: redirect}));
      }
    }
  });

  ngOnInit() {
    setTimeout(() => {
      this.loadingService.loading = false;
    }, 100);
  }

  ngOnDestroy() {
    this.nationalityManagerSuscription.unsubscribe();
  }

  setService() {
    if (this.dataNotClientService.getMainIsClient() && this.dataNotClientService.getIdentificationType() !== '4') {
      this.mainIsClient = true;
    }
    this.privateMode = (environment.mode === 'IN' || environment.mode === 'PF') ? true : false;
    this.dataService = this.privateMode ? this.dataClientService : this.dataNotClientService;
    const googleAnalytics = {
      is_joint_account: this.dataService.isIsJointAccount(),
      job_information: {
        workActivityType: this.dataService.getWorkActivityType() ? this.dataService.getWorkActivityType() : 'No informado',
        dateEntryWork: this.dataService.getDateEntryWork() ? this.dataService.getDateEntryWork() : 'No informado',
        companyName: this.dataService.getCompanyName() ? this.dataService.getCompanyName() : 'No informado',
        rucCompany: this.dataService.getRucCompany() ? this.dataService.getRucCompany() : 'No informado',
        profession: this.dataService.getProfession() ? this.dataService.getProfession() : 'No informado'
      }
    };
    this.store.dispatch(TagPage({text: 'nationality-information', extras: googleAnalytics}));
  }

  continue() {
    this.dataService.setNationality(this.formData.controls['nationality'].value);
    this.dataService.setResidenceCountry(this.formData.controls['residenceCountry'].value);
    this.dataService.setTaxResidence(this.formData.controls['taxResidence'].value);
    if (!this.privateMode) {
      this.dataNotClientService.setCountryBirth(this.formData.controls['countryBirth'].value);
    }

    if (this.dataService.isIsJointAccount()) {
      const redirect = this.privateMode ? StepsClientTypes.STEP_JOINT_NATIONALITY_CLIENT : StepsNotClientTypes.STEP_JOINT_NATIONALITY;
      return this.store.dispatch(UiActions.Navigate({ step: redirect }));
    } else {
      this.nationalityRequest();
    }
  }

  nationalityRequest() {
    this.loadingService.loading = true;

    const request: RequestNationalityManagerCreate = {
      client: {
        nationality: this.dataService.getNationality(),
        residenceCountry: this.dataService.getResidenceCountry(),
        taxResidence: this.dataService.getTaxResidence()
      }
    };

    if (!this.privateMode) {
      request.client.countryBirth = this.dataNotClientService.getCountryBirth();
    }

    this.store.dispatch(NationalityManagerCreateActions.getNationalityManagerCreate({ request }));
  }

  navigation(): StepsClientTypes | StepsNotClientTypes {
    if (this.privateMode) {
      if (this.dataClientService.getAccountType() === 'Ahorro') {
        return StepsClientTypes.STEP_INFORMATION_PEP;
      } else {
        return StepsClientTypes.STEP_INFORMATION_LABOR_CLIENT;
      }
    } else {
      return StepsNotClientTypes.STEP_INFORMATION_PEP;
    }
  }

  setForm() {
    this.preloadForm();

    // Esto lo tengo que hacer porque si no muto el json y elimina peru de countries
    this.countriesNationality = countries.map((country) => country);
    if (this.dataService.clientIsForeign()) {
      this.countriesNationality.shift();
    }
  }

  preloadForm() {
    const peru = this.countries.find((country) => country.iso = '4028');
    if (this.dataService.clientIsForeign()) {
      return this.formData = this._fb.group({
        countryBirth: [null],
        nationality: [ null, Validators.required],
        residenceCountry: [ null, Validators.required],
        taxResidence: [ null, Validators.required]
      });
    } else {
      this.formData = this._fb.group({
        countryBirth: [null],
        nationality: [ peru.iso, Validators.required],
        residenceCountry: [ peru.iso, Validators.required],
        taxResidence: [ peru.iso, Validators.required]
      });

      if (!this.mainIsClient) {
        this.formData.controls.countryBirth.setValue(peru.iso);
        this.formData.controls.countryBirth.setValidators([Validators.required]);
        this.formData.controls.countryBirth.updateValueAndValidity();
      }
      return this.formData;
    }
  }
}
